import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { useCart, eur } from "@/context/CartContext";
import { Loader2, ShoppingCart, ArrowLeft, Lock } from "lucide-react";

export default function Checkout() {
    const { items, subtotalCents, vatCents, totalCents, vatPercent, setQty, remove } = useCart();
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        if (items.length === 0) return;
        if (!form.email.trim()) { toast.error("Indique o seu email."); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/shop/checkout", {
                items: items.map((x) => (
                    x.bundle_id
                        ? { bundle_id: x.bundle_id, quantity: x.quantity }
                        : { product_id: x.product_id, quantity: x.quantity, selected_options: x.selected_options || null }
                )),
                customer: form,
                origin_url: window.location.origin,
            });
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                toast.error("Não foi possível iniciar o pagamento.");
                setLoading(false);
            }
        } catch (err) {
            toast.error("Falhou: " + formatApiError(err?.response?.data?.detail || err.message));
            setLoading(false);
        }
    };

    if (items.length === 0) {
        return (
            <div className="container-x py-24 text-center" data-testid="checkout-empty">
                <ShoppingCart className="h-10 w-10 mx-auto text-muted-foreground" />
                <p className="mt-4 text-muted-foreground">O seu carrinho está vazio.</p>
                <Link to="/loja" className="mt-6 inline-flex items-center gap-2 rounded-full bg-secondary px-5 py-2.5 text-sm font-bold text-secondary-foreground">Ir para a loja</Link>
            </div>
        );
    }

    return (
        <div className="container-x py-12" data-testid="checkout-page">
            <Link to="/loja" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"><ArrowLeft className="h-4 w-4" /> Continuar a comprar</Link>
            <h1 className="mt-3 font-heading text-3xl font-extrabold text-primary">Finalizar compra</h1>

            <div className="mt-8 grid gap-8 lg:grid-cols-2">
                <form onSubmit={submit} className="rounded-2xl border border-border bg-white p-6 space-y-5 h-fit">
                    <h2 className="font-heading text-lg font-bold text-primary">Os seus dados</h2>
                    {[
                        { k: "name", label: "Nome", type: "text", ph: "O seu nome" },
                        { k: "email", label: "Email", type: "email", ph: "email@exemplo.pt" },
                        { k: "phone", label: "Telemóvel", type: "tel", ph: "912345678" },
                    ].map((f) => (
                        <div key={f.k}>
                            <label className="block text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-2">{f.label}</label>
                            <input
                                type={f.type} value={form[f.k]} placeholder={f.ph}
                                onChange={(e) => setForm((s) => ({ ...s, [f.k]: e.target.value }))}
                                data-testid={`checkout-${f.k}`}
                                required={f.k === "email"}
                                className="w-full rounded-md border border-border bg-white px-4 py-2.5 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-secondary"
                            />
                        </div>
                    ))}
                    <button type="submit" disabled={loading} data-testid="checkout-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-secondary px-5 py-3 text-sm font-bold text-secondary-foreground transition hover:-translate-y-0.5 disabled:opacity-50">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                        {loading ? "A redirecionar…" : `Pagar ${eur(totalCents)}`}
                    </button>
                    <p className="text-xs text-muted-foreground text-center">Pagamento seguro processado pela Stripe.</p>
                </form>

                <div className="rounded-2xl border border-border bg-white p-6 h-fit">
                    <h2 className="font-heading text-lg font-bold text-primary mb-4">Resumo</h2>
                    <div className="space-y-4">
                        {items.map((it) => (
                            <div key={it.line_key} className="flex gap-3 items-center">
                                <div className="h-14 w-14 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                                    {it.image_url ? <img src={mediaUrl(it.image_url)} alt={it.name} className="h-full w-full object-cover" /> : null}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-primary truncate">
                                        {it.bundle_id ? <span className="mr-1 rounded-full bg-secondary/15 px-1.5 py-0.5 text-[9px] font-bold text-primary/70 uppercase tracking-wider">Pack</span> : null}
                                        {it.name}
                                    </div>
                                    {it.options_summary ? <div className="text-[11px] text-muted-foreground">{it.options_summary}</div> : null}
                                    <div className="text-xs text-muted-foreground">
                                        {eur(it.price_cents)} <span className="opacity-70">s/IVA</span> × {it.quantity}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input type="number" min="1" max="99" value={it.quantity} onChange={(e) => setQty(it.line_key, parseInt(e.target.value || "1", 10))} className="w-14 rounded border border-border px-2 py-1 text-sm" />
                                    <button onClick={() => remove(it.line_key)} className="text-xs text-red-500 hover:underline">remover</button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-5 pt-4 border-t border-border space-y-2">
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                            <span>Subtotal</span>
                            <span data-testid="checkout-subtotal">{eur(subtotalCents)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                            <span>IVA ({vatPercent}%)</span>
                            <span data-testid="checkout-vat">{eur(vatCents)}</span>
                        </div>
                        <div className="pt-2 border-t border-border flex items-center justify-between">
                            <span className="font-semibold text-primary">Total</span>
                            <span data-testid="checkout-total" className="font-heading text-2xl font-extrabold text-primary">{eur(totalCents)}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
